import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus, X, Wand2, Loader2 } from 'lucide-react';
import { createExperienceItem, createEducationItem, createProjectItem } from './resumeModel';

const DragHandle = ({ listeners, attributes }) => (
  <button
    className="cursor-grab active:cursor-grabbing text-[#9ca3af] hover:text-[#4b5563] p-1"
    {...listeners}
    {...attributes}
    data-testid="drag-handle"
  >
    <GripVertical size={16} />
  </button>
);

const AIButton = ({ onClick, loading, label }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="text-[10px] font-bold uppercase tracking-wider text-[#10b981] hover:text-[#059669] flex items-center gap-1 disabled:opacity-50 transition-colors"
    data-testid={`ai-generate-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    {loading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
    {loading ? 'Generating...' : label}
  </button>
);

const SectionEditor = ({ section, onChange, onRemove, onAIGenerate, aiLoading }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const updateField = (field, value) => onChange({ ...section, [field]: value });

  const updateItem = (index, updates) => {
    const items = [...(section.items || [])];
    items[index] = typeof items[index] === 'string' ? updates : { ...items[index], ...updates };
    onChange({ ...section, items });
  };

  const addItem = () => {
    const items = [...(section.items || [])];
    if (section.type === 'experience') items.push(createExperienceItem());
    else if (section.type === 'education') items.push(createEducationItem());
    else if (section.type === 'projects') items.push(createProjectItem());
    else items.push('');
    onChange({ ...section, items });
  };

  const removeItem = (index) => {
    const items = (section.items || []).filter((_, i) => i !== index);
    onChange({ ...section, items });
  };

  const updateBullet = (itemIdx, bulletIdx, value) => {
    const items = [...(section.items || [])];
    const bullets = [...(items[itemIdx].bullets || [])];
    bullets[bulletIdx] = value;
    items[itemIdx] = { ...items[itemIdx], bullets };
    onChange({ ...section, items });
  };

  const addBullet = (itemIdx) => {
    const items = [...(section.items || [])];
    const bullets = [...(items[itemIdx].bullets || []), ''];
    items[itemIdx] = { ...items[itemIdx], bullets };
    onChange({ ...section, items });
  };

  const removeBullet = (itemIdx, bulletIdx) => {
    const items = [...(section.items || [])];
    const bullets = (items[itemIdx].bullets || []).filter((_, i) => i !== bulletIdx);
    items[itemIdx] = { ...items[itemIdx], bullets };
    onChange({ ...section, items });
  };

  const inputClass = "w-full px-3 py-2 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors bg-white";
  const labelClass = "block text-[11px] uppercase tracking-wider font-semibold text-[#6b7280] mb-1";

  return (
    <div ref={setNodeRef} style={dragStyle} className="dashboard-card mb-3" data-testid={`section-${section.type}`}>
      {/* Section Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#f8fafc] border-b border-[#e5e7eb]">
        <div className="flex items-center gap-2">
          <DragHandle listeners={listeners} attributes={attributes} />
          <input
            value={section.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="font-satoshi font-bold text-sm text-[#0f1115] bg-transparent border-none focus:outline-none"
            data-testid={`section-title-${section.type}`}
          />
        </div>
        <div className="flex items-center gap-2">
          {section.type === 'summary' && onAIGenerate && (
            <AIButton onClick={() => onAIGenerate(section.id, 'summary')} loading={aiLoading === section.id} label="AI Write" />
          )}
          {section.type === 'skills' && onAIGenerate && (
            <AIButton onClick={() => onAIGenerate(section.id, 'skills')} loading={aiLoading === section.id} label="AI Suggest" />
          )}
          <button onClick={onRemove} className="text-[#9ca3af] hover:text-[#e11d48] p-1" data-testid={`remove-section-${section.type}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Section Body */}
      <div className="p-4 space-y-3">
        {/* Summary */}
        {section.type === 'summary' && (
          <textarea
            value={section.content || ''}
            onChange={(e) => updateField('content', e.target.value)}
            rows={3}
            placeholder="Write a 2-3 sentence summary of your professional background..."
            className={`${inputClass} resize-none`}
            data-testid="summary-input"
          />
        )}

        {/* Experience */}
        {section.type === 'experience' && (section.items || []).map((item, idx) => (
          <div key={item.id} className="border border-[#e5e7eb] p-3 space-y-2 relative">
            {section.items.length > 1 && (
              <button onClick={() => removeItem(idx)} className="absolute top-2 right-2 text-[#9ca3af] hover:text-[#e11d48]"><X size={14} /></button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Job Title</label><input value={item.title} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="Software Engineer" className={inputClass} /></div>
              <div><label className={labelClass}>Company</label><input value={item.company} onChange={(e) => updateItem(idx, { company: e.target.value })} placeholder="Company Name" className={inputClass} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className={labelClass}>Location</label><input value={item.location} onChange={(e) => updateItem(idx, { location: e.target.value })} placeholder="City, State" className={inputClass} /></div>
              <div><label className={labelClass}>Start Date</label><input value={item.startDate} onChange={(e) => updateItem(idx, { startDate: e.target.value })} placeholder="Jan 2022" className={inputClass} /></div>
              <div>
                <label className={labelClass}>End Date</label>
                <div className="flex items-center gap-2">
                  <input value={item.endDate} onChange={(e) => updateItem(idx, { endDate: e.target.value })} placeholder={item.current ? 'Present' : 'Dec 2024'} disabled={item.current} className={`${inputClass} ${item.current ? 'opacity-50' : ''} flex-1`} />
                  <label className="flex items-center gap-1 text-[10px] text-[#6b7280] whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={item.current} onChange={(e) => updateItem(idx, { current: e.target.checked, endDate: '' })} className="accent-[#002fa7]" />
                    Current
                  </label>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelClass}>Bullet Points</label>
                {onAIGenerate && item.title && (
                  <AIButton
                    onClick={() => onAIGenerate(section.id, 'bullets', { itemIdx: idx, title: item.title, company: item.company, existing: (item.bullets || []).filter(Boolean).join('; ') })}
                    loading={aiLoading === `${section.id}-${idx}`}
                    label="AI Bullets"
                  />
                )}
              </div>
              {(item.bullets || []).map((b, bi) => (
                <div key={`bullet-${idx}-${bi}-${b.slice(0, 8)}`} className="flex items-center gap-1 mb-1">
                  <span className="text-[#9ca3af] text-xs">-</span>
                  <input value={b} onChange={(e) => updateBullet(idx, bi, e.target.value)} placeholder="Describe your achievement with metrics..." className={`${inputClass} flex-1`} />
                  {item.bullets.length > 1 && <button onClick={() => removeBullet(idx, bi)} className="text-[#9ca3af] hover:text-[#e11d48]"><X size={12} /></button>}
                </div>
              ))}
              <button onClick={() => addBullet(idx)} className="text-xs text-[#002fa7] font-semibold mt-1 hover:underline">+ Add bullet</button>
            </div>
          </div>
        ))}

        {/* Education */}
        {section.type === 'education' && (section.items || []).map((item, idx) => (
          <div key={item.id} className="border border-[#e5e7eb] p-3 space-y-2 relative">
            {section.items.length > 1 && (
              <button onClick={() => removeItem(idx)} className="absolute top-2 right-2 text-[#9ca3af] hover:text-[#e11d48]"><X size={14} /></button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Degree</label><input value={item.degree} onChange={(e) => updateItem(idx, { degree: e.target.value })} placeholder="Bachelor of Science in CS" className={inputClass} /></div>
              <div><label className={labelClass}>School</label><input value={item.school} onChange={(e) => updateItem(idx, { school: e.target.value })} placeholder="University Name" className={inputClass} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Year</label><input value={item.year} onChange={(e) => updateItem(idx, { year: e.target.value })} placeholder="2020" className={inputClass} /></div>
              <div><label className={labelClass}>Details (optional)</label><input value={item.details} onChange={(e) => updateItem(idx, { details: e.target.value })} placeholder="GPA, honors, relevant coursework" className={inputClass} /></div>
            </div>
          </div>
        ))}

        {/* Skills / Certifications */}
        {(section.type === 'skills' || section.type === 'certifications') && (
          <div className="space-y-1">
            {(section.items || []).map((item, idx) => (
              <div key={`${section.type}-${idx}-${String(item).slice(0, 8)}`} className="flex items-center gap-1">
                <input
                  value={item}
                  onChange={(e) => { const items = [...section.items]; items[idx] = e.target.value; onChange({ ...section, items }); }}
                  placeholder={section.type === 'skills' ? 'e.g., Python, React, AWS' : 'e.g., AWS Solutions Architect'}
                  className={`${inputClass} flex-1`}
                />
                {section.items.length > 1 && <button onClick={() => removeItem(idx)} className="text-[#9ca3af] hover:text-[#e11d48]"><X size={12} /></button>}
              </div>
            ))}
          </div>
        )}

        {/* Projects */}
        {section.type === 'projects' && (section.items || []).map((item, idx) => (
          <div key={item.id} className="border border-[#e5e7eb] p-3 space-y-2 relative">
            {section.items.length > 1 && (
              <button onClick={() => removeItem(idx)} className="absolute top-2 right-2 text-[#9ca3af] hover:text-[#e11d48]"><X size={14} /></button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><label className={labelClass}>Project Name</label><input value={item.name} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="Project Name" className={inputClass} /></div>
              <div><label className={labelClass}>Technologies</label><input value={item.tech} onChange={(e) => updateItem(idx, { tech: e.target.value })} placeholder="React, Node.js, AWS" className={inputClass} /></div>
            </div>
            <div><label className={labelClass}>Description</label><input value={item.description} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Brief description of the project and your role" className={inputClass} /></div>
            <div><label className={labelClass}>URL (optional)</label><input value={item.url} onChange={(e) => updateItem(idx, { url: e.target.value })} placeholder="https://github.com/..." className={inputClass} /></div>
          </div>
        ))}

        {/* Add Item Button */}
        {['experience', 'education', 'skills', 'certifications', 'projects'].includes(section.type) && (
          <button onClick={addItem} className="w-full py-2 border border-dashed border-[#d1d5db] text-xs font-semibold text-[#6b7280] hover:text-[#002fa7] hover:border-[#002fa7] transition-colors flex items-center justify-center gap-1" data-testid={`add-item-${section.type}`}>
            <Plus size={14} /> Add {section.type === 'experience' ? 'Position' : section.type === 'education' ? 'Education' : section.type === 'projects' ? 'Project' : 'Item'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SectionEditor;
